import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import styled from 'styled-components'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import Text from 'components/_UI/Text'
import { ShieldCheck } from 'lucide-react'
import { FullyCentered, HorizontalCenter } from 'styled/alignment/Center'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent'
import teamsApi from 'api/requests/teams'
import producerProfilesApi from 'api/requests/producerProfiles'

export default function TeamGrantModal({ visible, teamId, existingGrants, onClose, onSuccess }) {
    const [scopeType, setScopeType] = useState('event');
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);

    const hasPanelGrant = (existingGrants || []).some(grant => grant.scope_type === 'panel');
    const grantedEventIds = (existingGrants || [])
        .filter(grant => grant.scope_type === 'event')
        .map(grant => grant.event?.id);

    useEffect(() => {
        fetchEvents();
    }, []);

    async function fetchEvents() {
        setLoadingEvents(true);

        try {
            const [{ data: activeData }, { data: expiredData }] = await Promise.all([
                producerProfilesApi.getMeEvents('active', 1, 50),
                producerProfilesApi.getMeEvents('expired', 1, 50),
            ]);
            const allEvents = [...(activeData?.data || []), ...(expiredData?.data || [])];
            setEvents(allEvents.filter(event => !grantedEventIds.includes(event.id)));
        } catch (error) {

        } finally {
            setLoadingEvents(false);
        }
    }

    async function onSubmit() {
        setErrorMessage(false);
        setLoading(true);

        try {
            await teamsApi.addGrant(teamId, {
                scope_type: scopeType,
                event_id: scopeType === 'event' ? selectedEvent?.value : undefined,
            });
            onSuccess();
        } catch ({ response }) {
            setErrorMessage(response?.data?.message)
        } finally {
            setLoading(false);
        }
    }

    const eventOptions = events.map(event => ({ value: event.id, label: event.name }));
    const canSubmit = scopeType === 'panel' ? !hasPanelGrant : !!selectedEvent;

    return (
        <Modal isOpen={visible} closeButton={true} onClose={onClose} size='medium'>
            <Container>
                <ModalHeader>
                    <IconCircle>
                        <ShieldCheck size={20} color={panelAccent(colors.secondaryPurple)} />
                    </IconCircle>
                    <HeaderTitle>Conceder acesso</HeaderTitle>
                </ModalHeader>

                <Column marginTop={'20px'}>
                    <SideTitle>Onde esta equipe vai ter acesso</SideTitle>

                    <ScopeOptions>
                        <ScopeOption
                            type='button'
                            $selected={scopeType === 'event'}
                            onClick={() => setScopeType('event')}
                        >
                            Evento específico
                        </ScopeOption>
                        <ScopeOption
                            type='button'
                            $selected={scopeType === 'panel'}
                            $disabled={hasPanelGrant}
                            disabled={hasPanelGrant}
                            onClick={() => !hasPanelGrant && setScopeType('panel')}
                        >
                            Painel inteiro
                        </ScopeOption>
                    </ScopeOptions>

                    {
                        hasPanelGrant && (
                            <Text color={colors.nightLight} marginTop={'6px'}>
                                Esta equipe já tem acesso ao painel inteiro.
                            </Text>
                        )
                    }

                    {
                        scopeType === 'event' && (
                            <EventFieldWrapper>
                                <Text color={colors.nightDark} type='bold' marginBottom={'6px'}>
                                    Evento
                                </Text>
                                <Select
                                    placeholder={loadingEvents ? 'Carregando eventos...' : 'Selecione um evento'}
                                    options={eventOptions}
                                    value={selectedEvent}
                                    onChange={setSelectedEvent}
                                    isLoading={loadingEvents}
                                    noOptionsMessage={() => 'Todos os eventos já têm acesso, ou você ainda não tem eventos'}
                                />
                            </EventFieldWrapper>
                        )
                    }

                    {
                        scopeType === 'panel' && (
                            <Text color={colors.nightLight} marginTop={'10px'}>
                                A equipe passa a ter acesso a todos os seus eventos, atuais e futuros — todos os membros com exatamente as permissões definidas para a equipe.
                            </Text>
                        )
                    }

                    {
                        errorMessage && (
                            <HorizontalCenter marginTop={'10px'}>
                                <Text color={colors.danger}>
                                    {errorMessage}
                                </Text>
                            </HorizontalCenter>
                        )
                    }
                </Column>

                {
                    loading ? (
                        <FullyCentered height='60px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <ButtonsContainer>
                            <Button
                                variant={'light'}
                                category='roundedBorded'
                                onClick={onClose}
                            >
                                Cancelar
                            </Button>

                            <Button
                                variant={!canSubmit ? 'disabled' : 'lightBlue'}
                                category='roundedBorded'
                                onClick={canSubmit ? onSubmit : undefined}
                            >
                                Conceder acesso
                            </Button>
                        </ButtonsContainer>
                    )
                }
            </Container>
        </Modal>
    )
}

const Container = styled(Column)`
    width: 100%;
    text-align: left;
    justify-content: flex-start;
    align-items: flex-start;
`;

const ScopeOptions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 10px;
    flex-wrap: wrap;
`;

const ScopeOption = styled.button`
    padding: 10px 16px;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected }) => $selected ? panelAccent(colors.secondaryPurple) : colors.neutralDark};
    background: ${({ $selected }) => $selected ? panelAccentAlpha(colors.occasionalPurple, '15') : '#fff'};
    color: ${({ $selected }) => $selected ? panelAccent(colors.secondaryPurple) : colors.nightDark};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    ${({ $disabled }) => $disabled && `
        opacity: .5;
        cursor: not-allowed;
    `}
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const IconCircle = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const SideTitle = styled.h4`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
    margin: 0 0 6px;
`;

const EventFieldWrapper = styled.div`
    width: 100%;
    margin-top: 14px;
`;

const ButtonsContainer = styled(Column)`
    margin-top: 20px;

    @media screen and (min-width: ${breakpoints.md}) {
        margin-top: 40px;
        flex-direction: row;

        button:first-child {
            margin-right: 16px;
        }
    }
`;
